import { PageController } from '@client/components/_core/layout/PageController';
import { DndTable } from '@client/components/_ui/data-display/DndTable';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SurfaceCard } from '@client/components/_ui/display/SurfaceCard';
import { TaggedList } from '@client/components/_ui/display/TaggedList';
import { EmptyState } from '@client/components/_ui/feedback/EmptyState';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { productVersionFormStore } from '@client/components/products-v2/store';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { type RouterOutputs, trpc } from '@client/trpc/client';
import { CONTACT_TYPE_TO_TAG, MAX_PAGE_SIZE } from '@shared/constants';
import { CONTACT_FIELD_TYPE_LABELS } from '@shared/constants/business/product';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { Users } from 'lucide-react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/contact-types',
)({
    staticData: {
        ui: {
            useParentTitle: false,
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ContactTypes />
        </PageController>
    );
}

type ContactTypeRow = RouterOutputs['productVersionContactType']['listProductVersionContactTypes']['rows'][number];

/** dnd-kit keys rows by string | number, so the bigint id is stringified at this boundary. */
type SortableContactTypeRow = Omit<ContactTypeRow, 'id'> & { id: string; contactTypeId: bigint };

function ContactTypes() {
    const orgId = useOrgId();
    const isReadOnly = productVersionFormStore.use.isReadonly();
    const { productId, productVersionId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { mutate: deleteContactType } = trpc.productVersionContactType.deleteProductVersionContactType.useMutation();
    const { mutate: reorderContactTypes } =
        trpc.productVersionContactType.reorderProductVersionContactTypes.useMutation();
    const navigate = useNavigate();

    // Ordering is the point of this list — it drives the section order on the claim's
    // Contacts tab — so it is a drag-sorted table, not a paged/sortable one.
    // Reorder must list every contact type on the version, so read past the default page size.
    const { data, isPending, error, refetch, isFetching } =
        trpc.productVersionContactType.listProductVersionContactTypes.useQuery({
            where: {
                productVersionId,
            },
            limit: MAX_PAGE_SIZE,
        });

    const rows: SortableContactTypeRow[] = (data?.rows ?? []).map((row) => ({
        ...row,
        id: row.id.toString(),
        contactTypeId: row.id,
    }));

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return (
        <>
            <Outlet />
            <SurfaceCard
                icon={Users}
                title="Contact types"
                subtitle={data ? `${data.total} configured` : undefined}
                padded={false}
                className="max-w-7xl"
                extra={
                    !isReadOnly && (
                        <Button.Add.Link
                            to="/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/contact-types/add"
                            params={{
                                orgId,
                                productId,
                                productVersionId,
                            }}
                            variant="tertiary"
                            size="small"
                            label="New contact type"
                        />
                    )
                }
            >
                <div className={isReadOnly ? '[&_td:first-child_button]:hidden' : undefined}>
                    <DndTable<SortableContactTypeRow>
                        size="small"
                        lastInSurface
                        rowKey="id"
                        loading={isPending}
                        pagination={false}
                        dataSource={rows}
                        locale={{ emptyText: <EmptyState.Table title="No contact types configured" /> }}
                        onDragEnd={(keys) =>
                            reorderContactTypes(
                                {
                                    productVersionId,
                                    orderedIds: keys.map((key) => BigInt(key)),
                                },
                                {
                                    onSuccess() {
                                        void refetch();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            )
                        }
                        onRow={({ contactTypeId }) => ({
                            className: 'group cursor-pointer',
                            onClick(e) {
                                navigate(
                                    {
                                        to: '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/contact-types/edit/$contactTypeId',
                                        params: {
                                            orgId,
                                            productId,
                                            productVersionId,
                                            contactTypeId,
                                        },
                                    },
                                    e,
                                );
                            },
                        })}
                        columns={[
                            {
                                key: 'name',
                                title: 'Name',
                                dataIndex: 'name',
                                width: 240,
                                ellipsis: true,
                                className: 'font-medium',
                            },
                            {
                                key: 'systemType',
                                title: 'Role',
                                width: 160,
                                render(_, row) {
                                    return row.systemType ? (
                                        <Tag>{CONTACT_TYPE_TO_TAG[row.systemType]}</Tag>
                                    ) : (
                                        <span className="text-subtle-foreground">None</span>
                                    );
                                },
                            },
                            {
                                key: 'fields',
                                title: 'Fields',
                                render(_, row) {
                                    return (
                                        <TaggedList
                                            items={row.ProductVersionContactTypeFields}
                                            accessor={(item) => CONTACT_FIELD_TYPE_LABELS[item.type]}
                                            emptyText="No fields"
                                        />
                                    );
                                },
                            },
                            ...(isReadOnly
                                ? []
                                : [
                                      {
                                          key: 'actions',
                                          align: 'right' as const,
                                          className: 'w-16',
                                          render(_: unknown, row: SortableContactTypeRow) {
                                              // Destructive row action stays quiet until the row is hovered or
                                              // focused; keyboard users still reach it through focus-within.
                                              return (
                                                  <span className="inline-flex opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                                                      <Button.Delete
                                                          onConfirm={(onSuccess) =>
                                                              deleteContactType(
                                                                  { id: row.contactTypeId },
                                                                  {
                                                                      onSuccess() {
                                                                          onSuccess();
                                                                          void trpcUtils.productVersionContactType.invalidate();
                                                                      },
                                                                      onError(error) {
                                                                          errorMessage.show(error);
                                                                      },
                                                                  },
                                                              )
                                                          }
                                                      />
                                                  </span>
                                              );
                                          },
                                      },
                                  ]),
                        ]}
                    />
                </div>
            </SurfaceCard>
        </>
    );
}
